import { Link } from 'react-router-dom';
import { Plus, Send } from 'lucide-react';
import type { CampaignResponse } from '@outreach/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCampaigns } from '@/features/campaigns/useCampaigns';

const STATUS_VARIANT: Record<string, 'secondary' | 'success' | 'muted' | 'destructive'> = {
  draft: 'muted',
  scheduled: 'secondary',
  sending: 'secondary',
  paused: 'muted',
  completed: 'success',
  completed_with_errors: 'destructive',
  cancelled: 'muted',
  failed: 'destructive',
};

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

function CampaignRow({ campaign }: { campaign: CampaignResponse }) {
  return (
    <Link
      to={`/campaigns/${campaign.id}`}
      className="flex items-center justify-between gap-4 border-b px-4 py-3 text-sm last:border-b-0 hover:bg-muted/50"
    >
      <div className="min-w-0">
        <p className="truncate font-medium" title={campaign.name}>{campaign.name}</p>
        <p className="text-xs text-muted-foreground">
          {campaign.channels.join(', ')} · rev {campaign.revision}
        </p>
      </div>
      <Badge variant={STATUS_VARIANT[campaign.status] ?? 'muted'}>
        {statusLabel(campaign.status)}
      </Badge>
    </Link>
  );
}

/** Campaign list with lifecycle status; opens the composer/review per campaign. */
export function CampaignsPage() {
  const { data, isLoading } = useCampaigns();
  const campaigns = data?.items ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Draft, schedule and send outreach.</p>
        <Button asChild size="sm">
          <Link to="/campaigns/new">
            <Plus className="size-4" /> New campaign
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Loading campaigns…
          </CardContent>
        </Card>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex size-10 items-center justify-center rounded-full bg-muted">
              <Send className="size-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No campaigns yet.</p>
            <Button asChild variant="outline" size="sm">
              <Link to="/campaigns/new">Create your first campaign</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {campaigns.map((c) => (
              <CampaignRow key={c.id} campaign={c} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
